import { trigger } from '@angular/animations';
import { AsyncPipe, Location, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
  Section,
  TenantDto,
} from '@nusign/picflow-backend-types';
import {
  Observable,
  Subject,
  catchError,
  filter,
  firstValueFrom,
  map,
  of,
  retry,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { fadeToIf } from '../../../animations/fade-to.if.animation';
import { fadeIf } from '../../../animations/fade.if.animation';
import { moveDownIf } from '../../../animations/move-down.if.animation';
import { moveLeftArrowIf } from '../../../animations/move-left-arrow.if.animation';
import { moveRightArrowIf } from '../../../animations/move-right-arrow.if.animation';
import { moveUpIf } from '../../../animations/move-up.if.animation';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import { assetLoadSettings, assetSideMenuSize, doubleClickTime, getBaseZoom } from '../../../consts';
import { VideoViewerControlsComponent } from '../../../gallery/components/video-viewer-controls/video-viewer-controls.component';
import { AssetPageDataService } from '../../../gallery/services/asset-page-data.service';
import { AssetTransitionService } from '../../../gallery/services/asset-transition.service';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { getAllowedDownloadSizes } from '../../../lib/convert-gallery-download-size';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { elementIsInputLike } from '../../../lib/element-is-input-like';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { getEventPath } from '../../../lib/get-event-path';
import { getFullPath } from '../../../lib/get-full-path';
import { isMetaKey } from '../../../lib/is-meta-key';
import { objectsMatch } from '../../../lib/objects-match';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AssetSupportsTransparencyPipe } from '../../../pipes/asset-supports-transparency.pipe';
import { BlurHashPipe } from '../../../pipes/blur-hash.pipe';
import { AssetDisplayService } from '../../../services/asset-display.service';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { AssetMoveService } from '../../../services/asset-move.service';
import { AssetPreGenerateService } from '../../../services/asset-pre-generate.service';
import { CommentService } from '../../../services/comment.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { ToastService } from '../../../services/toast.service';
import { UpdateService } from '../../../services/update.service';
import { UploadService } from '../../../services/upload.service';
import { VideoTimeService } from '../../../services/video-time.service';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { ClickOutsideDirective } from '../../../shared-extra/directives/click-outside.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { AnnotationControlsComponent } from '../../components/annotation-controls/annotation-controls.component';
import { AssetCommentFacesComponent } from '../../components/asset-comment-faces/asset-comment-faces.component';
import { AssetFeedbackHintsComponent } from '../../components/asset-feedback-hints/asset-feedback-hints.component';
import { AssetHeaderComponent } from '../../components/asset-header/asset-header.component';
import { AssetInfoComponent } from '../../components/asset-info/asset-info.component';
import { AssetPageFooterComponent } from '../../components/asset-page-footer/asset-page-footer.component';
import { AssetSideMenuComponent } from '../../components/asset-side-menu/asset-side-menu.component';
import { CommentThread, CommentsComponent } from '../../components/comments/comments.component';
import { CreateAnnotationComponent } from '../../components/create-annotation/create-annotation.component';
import { CreateCommentOverlayComponent } from '../../components/create-comment-overlay/create-comment-overlay.component';
import { ImageViewerControlsComponent } from '../../components/image-viewer-controls/image-viewer-controls.component';
import { ImageViewerPreviewComponent } from '../../components/image-viewer-preview/image-viewer-preview.component';
import { ImageViewerComponent } from '../../components/image-viewer/image-viewer.component';
import { ThumbnailBarComponent } from '../../components/thumbnail-bar/thumbnail-bar.component';
import { VideoTimeData, VideoViewerComponent } from '../../components/video-viewer/video-viewer.component';
import { ViewAnnotationComponent } from '../../components/view-annotation/view-annotation.component';
import { getAnnotationBounds } from '../../lib/get-annotation-bounds';
import {
  getPointCommentPosition,
  getShapeCommentPosition,
  relativeToAbsolutePosition,
} from '../../lib/get-annotation-positions';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';
import { AnnotationColor } from '../../types/annotation-color';
import { AnnotationTool } from '../../types/annotation-tool';
import { AssetPageData } from '../../types/asset-page-data';
import { Shape, SimpleAnnotation } from '../../types/shapes';

@Component({
  selector: 'app-asset-page',
  templateUrl: './asset-page.component.html',
  styleUrls: ['./asset-page.component.scss'],
  animations: [
    trigger('arrowLeft', moveLeftArrowIf),
    trigger('arrowRight', moveRightArrowIf),
    trigger('assetHeader', moveDownIf),
    trigger('assetFooter', moveUpIf),
    trigger('assetControls', moveDownIf),
    trigger('thumbnailBar', moveUpIf),
    trigger('assetFeedbackHints', fadeIf),
    trigger('animationMode', fadeIf),
    trigger('blurhash', fadeToIf(0.35)),
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropComponent,
    AssetHeaderComponent,
    ResizeDirective,
    ImageViewerControlsComponent,
    SvgComponent,
    ImageViewerComponent,
    LoadingIndicatorComponent,
    ImageViewerPreviewComponent,
    CreateAnnotationComponent,
    ViewAnnotationComponent,
    AssetPageFooterComponent,
    AnnotationControlsComponent,
    AssetFeedbackHintsComponent,
    AssetSideMenuComponent,
    AssetInfoComponent,
    ThumbnailBarComponent,
    AssetVersionUrlPipe,
    AsyncPipe,
    VideoViewerComponent,
    VideoViewerControlsComponent,
    ClickOutsideDirective,
    CanDisplayAssetPipe,
    AssetSupportsTransparencyPipe,
    CommentsComponent,
    CreateCommentOverlayComponent,
    AssetCommentFacesComponent,
    BlurHashPipe,
  ],
})
export class AssetPageComponent implements OnInit, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private state = inject(StateService);
  private feedbackService = inject(AssetFeedbackService);
  private updateService = inject(UpdateService);
  private assetDisplayService = inject(AssetDisplayService);
  private galleryService = inject(GalleryService);
  private platformId = inject<string>(PLATFORM_ID);
  private uploadService = inject(UploadService);
  private toasts = inject(ToastService);
  private location = inject(Location);
  private storage = inject(StorageService);
  private contextService = inject(ContextMenuService);
  private dialog = inject(DialogService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private assetPageDataService = inject(AssetPageDataService);
  private versionUrlPipe = inject(AssetVersionUrlPipe);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private videoTimeService = inject(VideoTimeService);
  private assetMoveService = inject(AssetMoveService);
  public commentService = inject(CommentService);
  private permissionService = inject(PermissionService);
  private assetTransitionService = inject(AssetTransitionService);

  public commentThreadCount$: Observable<number>;

  @ViewChild('assetViewerSection', { static: false }) assetViewerSection: ElementRef;
  @ViewChild('createAnnotation', { static: false }) createAnnotation: CreateAnnotationComponent;
  @ViewChild('comments', { static: false }) comments: CommentsComponent;
  @ViewChildren(VideoViewerComponent) videoViewers: QueryList<VideoViewerComponent>;
  activeVideoViewer: VideoViewerComponent;
  gallery: GalleryDto = getAllRouteData(this.route.snapshot).gallery;
  accessLevel: GalleryAccessLevel = getAllRouteData(this.route.snapshot).accessLevel;
  isGalleryGuest: boolean = getAllRouteData(this.route.snapshot).isGalleryGuest;
  isTeamMember: boolean;
  dragging = false;
  disableClickOutsideThreads = true;
  backupZoomSettings: { scale: number; offset: Rect };
  zooming = false;
  previousTouches: Rect[];
  sections: Section[];
  assets: AssetDto[];
  assetDatas: AssetPageData[] = [];
  active: AssetPageData;
  containerleft = 0;
  _containerOffset = 0;
  containerOffset = 0;
  containerVelocity = 0;
  noContainerAnimation = true;
  previousTouchStartTime = 0;
  sideMenuOpen: boolean;
  sideMenuWillBeOpen: boolean;
  touchStartPos: Rect;
  clientMode = getFullPath(this.route.snapshot)[0] !== 'a';
  currentLabel: GalleryColorLabel;
  controlsVisible = true;
  isDesktop = ScreenSizes.screenSize() === 'desktop';
  currentNumberOfTouches = 0;
  touchStartTime: number;
  annotationMode = false;
  createAnnotationShapes: Shape[] = null;
  commentPlacementMode: 'place' | 'drag';
  commentPlaceStartPosition: Rect;
  commentPlaceHoverRect: Rect;
  commentPlacePosition: Rect;
  commentPlacePositionOverridden: boolean;
  annotationTool: AnnotationTool = 'polygon';
  annotationColor: AnnotationColor = 'blue';
  fitButton: 'width' | 'height';
  annotationBeingViewed: SimpleAnnotation;
  mobileAnnotationViewingMode = false;
  threadBeingViewedTime: number;
  showThumbnailBar = false;
  closed = false;
  touchDragDirMode: 'x' | 'y';
  isTouchDevice: boolean;
  showWebGl: boolean;
  backgroundOpacity = 1;
  clearAssets = true;
  platformBrowser = isPlatformBrowser(this.platformId);
  showVersions: boolean;
  versionId: string;
  container: Rect;
  customMenuPos = new Rect();
  copyrightMessage: string;
  showCustomMenu = false;
  mouseHasMoved = false;
  mouseIsDown = false;
  lastShowControlsTime = 0;
  allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[] = [];
  colorLabels: GalleryColorLabel[] = [];
  autoHideControls = true;
  sideMenuMode: 'info' | 'activity';
  neverHideControls = false;
  colorLabelMenuOpen = false;
  dialogOpen = false;
  webglLoaded = false;
  videoTime: VideoTimeData;
  videoFocused = false;
  showLoadingIndicator = true;
  loadingIndicatorTimeout: NodeJS.Timeout;
  autoplayOnLoad = false;
  cachedScale: number;
  canZoom: boolean;
  destroyed$ = new Subject<void>();
  showCreateCommentOverlay = false;
  createCommentOverlayPosition: Rect;
  commentPlaceAlignment: 'left' | 'right' | 'center';
  commentMessage = { text: '', addVideoFramePosition: false, private: false };
  selectedThreadId: string;
  hoverThreadId: string;
  showResolved = false;
  spaceKeyDown = false;

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    if (this.dialog.dialog$.value) return;
    const metaKey = isMetaKey(event);

    if (event.code === 'Space') {
      this.spaceKeyDown = true;
      this.check.detectChanges();
    }

    if (event.key === 'Escape') {
      if ((this.commentPlacePosition && !this.createAnnotationShapes?.length) || this.commentPlacementMode) {
        if (!this.showCreateCommentOverlay) {
          this.switchPlaceComment(false);
        }
        this.stopCommentAtPosition();
      } else if (this.selectedThreadId) {
        this.comments?.selectThread(undefined);
      } else if (this.annotationMode) {
        this.stopAnnotation();
        this.analytics.reportKeyboardShortcut('exit-annotation-mode', 'asset-page');
      } else if (this.annotationBeingViewed) {
        this.viewAnnotation(undefined);
        this.analytics.reportKeyboardShortcut('exit-annotation-viewing-mode', 'asset-page');
      } else if (this.showThumbnailBar) {
        this.showThumbnailBar = false;
      } else {
        this.analytics.reportKeyboardShortcut('exit-asset-page', 'asset-page');
        this.close();
      }
      this.check.detectChanges();
    }

    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    if (event.key === 'ArrowRight') {
      if (this.annotationMode) return;
      if (this.videoFocused) return;
      this.noContainerAnimation = true;
      this.switchAsset(1);
      setTimeout(() => {
        this.noContainerAnimation = false;
        this.check.detectChanges();
      }, 100);
    }
    if (event.key === 'ArrowLeft') {
      if (this.annotationMode) return;
      if (this.videoFocused) return;
      this.noContainerAnimation = true;
      this.switchAsset(-1);
      setTimeout(() => {
        this.noContainerAnimation = false;
        this.check.detectChanges();
      }, 100);
    }
    if (event.code === 'Tab') {
      event.preventDefault();
      if (this.annotationMode) return;
      if (this.annotationBeingViewed) return;
      this.showThumbnailBar = !this.showThumbnailBar;
      this.analytics.reportKeyboardShortcut('toggle-thumbnails', 'asset-page');
      this.check.detectChanges();
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.annotationMode) return;
      if (this.annotationBeingViewed) return;
      this.showThumbnailBar = true;
      this.analytics.reportKeyboardShortcut('show-thumbnails', 'asset-page');
      this.check.detectChanges();
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.showThumbnailBar = false;
      this.analytics.reportKeyboardShortcut('hide-thumbnails', 'asset-page');
      this.check.detectChanges();
    }

    if ((event.key === '=' || event.key === '+') && metaKey) {
      event.preventDefault();
      if (this.active.asset.type === 'video') return;
      this.onScale('in');
      this.analytics.reportKeyboardShortcut('zoom-in', 'asset-page');
    }
    if (event.key === '-' && metaKey) {
      event.preventDefault();
      if (this.active.asset.type === 'video') return;
      this.onScale('out');
      this.analytics.reportKeyboardShortcut('zoom-out', 'asset-page');
    }

    if (event.key === '0' && metaKey) {
      if (this.active.asset.type === 'video') return;
      this.onScale('full');
      this.analytics.reportKeyboardShortcut('zoom-to-full', 'asset-page');
    }

    if (event.key === 'j' && metaKey) {
      this.neverHideControls = true;
      this.showControls();
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent) {
    if (event.code === 'Space') {
      this.spaceKeyDown = false;
    }
    const metaKey = isMetaKey(event);
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;
    if (!metaKey) {
      if (event.key === 'f') {
        this.toggleLike();
        this.analytics.reportKeyboardShortcut('toggle-like', 'asset-page');
      }
      if (event.key === 'a') {
        if (this.active.asset.status === 'approved') {
          this.updateAssetStatus('no_status', true);
        } else {
          this.updateAssetStatus('approved', true);
        }
      }
      if (event.key === 'x') {
        if (this.active.asset.status === 'rejected') {
          this.updateAssetStatus('no_status', true);
        } else {
          this.updateAssetStatus('rejected', true);
        }
      }
      if (event.key === 'c') {
        if (this.allowedAndEnabledFeedbackOptions.includes('comment')) {
          this.toggleCommentPlacementMode();
          this.analytics.reportKeyboardShortcut('toggle-comment-placement-mode', 'asset-page');
        }
      }
      if (event.key === 'i') {
        this.toggleSideMenu('info');
        this.analytics.reportKeyboardShortcut('toggle-info-sidebar', 'asset-page');
      }
      if (!this.annotationMode) {
        if (event.key === 'r') {
          this.updateColorLabel('red', true);
        }
        if (event.key === 'o') {
          this.updateColorLabel('orange', true);
        }
        if (event.key === 'g') {
          this.updateColorLabel('green', true);
        }
        if (event.key === 'b') {
          this.updateColorLabel('blue', true);
        }
        if (event.key === 'p') {
          this.updateColorLabel('purple', true);
        }
        if (event.key === 'n') {
          this.updateColorLabel(null, true);
        }
        if (event.key === 'd') {
          if (this.active.asset.type !== 'video' && this.allowedAndEnabledFeedbackOptions.includes('annotation')) {
            this.startAnnotation();
            this.analytics.reportKeyboardShortcut('start-annotation', 'asset-page');
          }
        }
      }
    }
  }

  @HostListener('window:resize', ['$event']) async winResize() {
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';
    if (!this.active) return;
    this.noContainerAnimation = true;
    this.calcAssetSizes();
    this.container = this.getContainerSize();
    this.calcContainerOffset();
    await sleep(50);
    this.noContainerAnimation = false;
    this.commentPlacePositionOverridden = false;
    this.calcCommentOverlayPosition();
    this.check.detectChanges();
  }

  @HostListener('document:mouseout', ['$event']) async mouseOut() {
    if (!this.isDesktop) return;
    if (this.sideMenuOpen) return;
    if (this.colorLabelMenuOpen) return;
    const exitTime = Date.now();
    await sleep(250);
    if (exitTime < this.lastShowControlsTime) return;
    if (this.dialogOpen) return;
    if (this.contextService.onContextOpen.value) return;
    if (this.neverHideControls) return;
    this.controlsVisible = false;
    this.check.detectChanges();
  }

  @HostListener('document:mouseover', ['$event']) async mouseIn() {
    if (!this.isDesktop) return;
    this.lastShowControlsTime = Date.now();
    this.check.detectChanges();
  }

  public growthBook = inject(GROWTHBOOK);

  private dealWithSidebarFromUrl() {
    let sideMenuMode =
      this.route.snapshot.fragment === 'comments'
        ? 'activity'
        : this.route.snapshot.fragment === 'info'
          ? 'info'
          : this.storage.get('asset-sidebar');
    if (this.accessLevel === 'view_only' && sideMenuMode === 'activity') sideMenuMode = undefined;
    this.sideMenuWillBeOpen = !!sideMenuMode;
    return sideMenuMode;
  }

  private async updateAdd(asset: AssetDto) {
    if (this.assets.find(img => img.id === asset.id)) return;
    if (!this.active) return;
    this.noContainerAnimation = false;
    this.clearAssets = true;
    this.check.detectChanges();
    const assets = this.assets.slice();
    assets.push(asset);
    const resp = await firstValueFrom(
      this.assetPageDataService.get(this.gallery, this.active.asset.id, this.route.snapshot.queryParams)
    );
    if (!resp) return;
    this.assetChange(this.active.asset, resp.assets, this.sections);
    this.noContainerAnimation = true;
    this.check.detectChanges();
  }

  private async updateRemove(asset: AssetDto) {
    const assets = this.assets.slice();
    const index = assets.findIndex(img => img.id === asset.id);
    if (index !== -1) {
      this.noContainerAnimation = false;
      this.clearAssets = true;
      this.clearAssets = true;
      assets.splice(index, 1);
      if (this.active && this.active.asset.id === asset.id) this.afterDelete();
      this.clearAssets = true;
      this.assetChange(this.active.asset, assets, this.sections);
      this.noContainerAnimation = true;
      this.check.detectChanges();
    }
  }

  private calcPermissions() {
    this.allowedAndEnabledFeedbackOptions = this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.accessLevel
    );
    this.colorLabels = this.gallery.color_labels;
    if (!this.permissionService.canDisplayElement(billingScopes.asset.customLabels))
      this.colorLabels = defaultColorLabels;
    if (this.active) {
      this.currentLabel = this.colorLabels.find(l => l.color === this.active.asset.color_label);
    }
    this.check.detectChanges();
  }

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    const sideMenuMode = this.dealWithSidebarFromUrl();
    if (this.sideMenuWillBeOpen) {
      this.sideMenuMode = sideMenuMode;
      this.noContainerAnimation = true;
      this.sideMenuOpen = true;
      // this.toggleSideMenu(sideMenuMode);
    }
    this.sideMenuWillBeOpen = false;
    this.container = this.getContainerSize();
  }

  async ngOnInit() {
    const tenantId = this.gallery['tenant']?.['id'] || this.gallery['tenant'];
    this.isTeamMember = !!this.state.tenants?.find(t => t.id === tenantId);

    this.calcPermissions();

    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      if (!(event instanceof NavigationEnd)) return;
      const sideMenuMode = this.dealWithSidebarFromUrl();
      if (this.sideMenuWillBeOpen && this.sideMenuMode !== sideMenuMode) {
        this.toggleSideMenu(sideMenuMode);
      }
      this.sideMenuWillBeOpen = false;
      this.check.detectChanges();
    });

    this.route.data.pipe(takeUntil(this.destroyed$)).subscribe(data => {
      this.autoplayOnLoad = this.route.snapshot.queryParams.autoplay;
      this.assetChange(data.data.asset, data.data.assets, data.data.sections);
      this.accessLevel = getAllRouteData(this.route.snapshot).accessLevel;
      this.check.detectChanges();
    });

    this.assetMoveService.assetMove$.pipe(takeUntil(this.destroyed$), filter(Boolean)).subscribe(event => {
      const asset = this.assets.find(a => a.id === event.assetId);
      if (!asset) return;
      this.onMovedAsset(asset);
    });

    this.updateService.onAssetUpdate.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      if (!this.active) return;
      if (!event) return;
      if (event.name === 'asset.created') {
        this.updateAdd(event.asset);
      } else if (event.name === 'asset.deleted') {
        this.updateRemove(event.asset);
      } else if (event.name === 'asset.updated') {
        const index = this.assets.findIndex(img => img.id === event.asset.id);
        if (index !== -1) {
          Object.assign(this.assets[index], event.asset);
          this.currentLabel = this.colorLabels.find(l => l.color === this.active.asset.color_label);
        } else {
          this.updateAdd(event.asset);
        }
      }
      this.check.detectChanges();
    });

    this.galleryService.onGalleryUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      const tenant = this.gallery.tenant;
      Object.assign(this.gallery, gallery);
      this.gallery.tenant = tenant;
      this.calcPermissions();
    });

    this.dialog.dialog$.pipe(takeUntil(this.destroyed$)).subscribe(dialog => {
      this.dialogOpen = !!dialog;
      this.check.detectChanges();
    });

    if (isPlatformServer(this.platformId)) return;

    this.showControls();
    this.check.detectChanges();

    await sleep(0);
    this.noContainerAnimation = false;
    this.check.detectChanges();
    if (!this.assetViewerSection) await sleep(100);
    if (!this.assetViewerSection) return;
    this.assetViewerSection.nativeElement.addEventListener('wheel', this.onWheel.bind(this));
  }

  private enableDragging() {
    this.dragging = true;
    this.disableClickOutsideThreads = true;
    this.check.detectChanges();
  }

  private disableDragging() {
    this.dragging = false;
    this.check.detectChanges();
    setTimeout(() => {
      this.disableClickOutsideThreads = false;
      this.check.detectChanges();
    }, 10);
  }

  private async assetChange(asset: AssetDto, assets: AssetDto[], sections: Section[]) {
    if (!asset) return;
    if (this.active?.asset && asset.id === this.active.asset.id && !this.clearAssets) return;
    if (asset.type !== 'video') this.videoTime = undefined;
    this.activeVideoViewer?.pause();
    this.backupZoomSettings = undefined;
    if (this.commentPlacementMode !== 'place') {
      this.commentPlacementMode = undefined;
    }
    this.selectedThreadId = undefined;
    this.hoverThreadId = undefined;
    this.commentMessage.text = '';
    this.commentMessage.private = false;
    this.commentMessage.addVideoFramePosition = asset.type === 'video';

    this.commentThreadCount$ = this.commentService
      .getComments(asset.id, this.gallery.id)
      .pipe(map(comments => comments.filter(c => !c.parent_comment).length));

    this.sections = sections;
    this.showWebGl = false;
    this.disableDragging();
    this.annotationBeingViewed = undefined;
    this.mobileAnnotationViewingMode = false;
    if (
      this.clearAssets ||
      this.assets[0]?.section !== assets[0]?.section ||
      !this.assets.find(img => img.id === asset.id)
    ) {
      this.assets = assets;
      this.clearAssets = false;
      this.assetDatas = this.assets.map(img => this.addAssetPageData(img));
      this.check.detectChanges();
    }
    this.assetDatas.forEach(d => (d.hideAsset = false));
    this.containerOffset = 0;
    this.containerVelocity = 0;
    this.active = this.assetDatas.find(d => d.asset.id === asset.id);
    this.check.detectChanges();

    if (!this.active) {
      // this can happen if there is a filter in the url that filters out the asset
      return this.close();
    }

    this.canZoom = this.active.asset.type === 'image' && this.active.offsetScale < 1 / devicePixelRatio();

    if (this.cachedScale && this.cachedScale === 1) {
      this.active.scale = this.cachedScale;
      this.calcAssetSizes();
    }

    this.doShowLoadingIndicator();

    setTimeout(() => {
      this.activeVideoViewer = this.videoViewers?.find(v => v.asset.id === this.active.asset.id);
      this.check.detectChanges();
      const el: HTMLImageElement = document.querySelector('.asset-container.active img');
      if (!el) return;
      if (!el.complete) return;
      this.imageLoaded();
    }, 0);
    this.assetTransitionService.currentAsset = asset;
    this.currentLabel = this.colorLabels.find(l => l.color === asset.color_label);
    this.handleContainerOffset();
    this.calcAssetSizes();
  }

  private calcContainerOffset() {
    if (!this.active) return;
    const index = this.assetDatas.findIndex(data => data.asset.id === this.active.asset.id);
    if (index === -1) return;
    this.containerleft = index * -this.container.width;
    this.check.detectChanges();
  }

  private async handleContainerOffset() {
    this.calcContainerOffset();
    const active = this.active;
    if (!active) return;
    active.active = true;
    this.check.detectChanges();
    await sleep(20); // giving active asset time to start loading
    if (active !== this.active) return;
    this.assetDatas.forEach(d => (d.active = false));
    active.active = true;
    const index = this.assets.findIndex(img => img.id === active.asset.id);

    for (let i = -assetLoadSettings.assetPagePreloadBefore; i <= assetLoadSettings.assetPagePreloadAfter; i++) {
      if (this.assetDatas[index + i]) {
        this.assetDatas[index + i].active = true;
      }
    }
    const toPreGenerate: AssetPageData[] = [];

    for (
      let i = -(assetLoadSettings.assetPagePreGenerateBefore + assetLoadSettings.assetPagePreloadBefore);
      i < -assetLoadSettings.assetPagePreloadBefore;
      i++
    ) {
      const data = this.assetDatas[index + i];
      if (data?.asset.version_data.data?.width) toPreGenerate.push(data);
    }

    for (
      let i = assetLoadSettings.assetPagePreloadAfter + 1;
      i <= assetLoadSettings.assetPagePreloadAfter + assetLoadSettings.assetPagePreGenerateAfter;
      i++
    ) {
      const data = this.assetDatas[index + i];
      if (data?.asset.version_data.data?.width) toPreGenerate.push(data);
    }
    this.check.detectChanges();

    toPreGenerate.forEach(async data => {
      if (!canDisplayAsset(data.asset)) return;
      const url = await this.versionUrlPipe.transform(
        data.asset.version_data,
        data.asset.version_data.uuid,
        data.asset.version_data['status'],
        data.asset.version_data.watermark_uuid,
        data.asset.version_data.data.width * data.offsetScale,
        false,
        data.asset.watermark_enabled,
        false
      );
      this.assetPreGenerateService.preloadAsset(url);
    });
  }

  private addAssetPageData(asset: AssetDto): AssetPageData {
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(
      asset.version_data,
      this.getContainerSize(asset)
    );
    const _data = asset.version_data.data?.width ? asset.version_data.data : this.getDataBackup();
    const offsetScale = boundedContainer.width / (_data.width || 1);

    const data: AssetPageData = {
      asset: asset,
      offset: new Rect(),
      _offsetY: 0,
      scale: undefined,
      offsetScale,
      active: false,
      hideAsset: false,
      crop: new Rect(),
    };

    data.scale = this.getBaseZoom(data);
    this.calcAssetSizes(data);
    return data;
  }

  private onWheel(event: WheelEvent) {
    if (this.active.asset.type === 'video') return;
    this.zooming = true;
    event.preventDefault();

    const { scale, offsetChange } = this.assetDisplayService.onWheel(
      event,
      this.getContainerSize(),
      this.active.scale,
      this.active.offsetScale
    );

    this.active.scale = scale;
    this.active.offset = this.active.offset.add(offsetChange);

    this.onScale(undefined);
    this.stopZooming();
    this.check.detectChanges();
    return false;
  }

  private stopZooming = debounce(() => {
    this.zooming = false;
    this.calcAssetSizes();
    this.check.detectChanges();
  }, 60);

  public getScalePercent(data: AssetPageData, scale = data.scale) {
    return Math.round(data.offsetScale * 100 * scale * devicePixelRatio());
  }

  onWebGlAssetLoaded() {
    this.webglLoaded = true;
    this.check.detectChanges();
  }

  onScale(dir: string) {
    this.active.scale =
      this.assetDisplayService.dirToScale(
        this.active.asset.version_data,
        dir,
        this.getContainerSize(),
        this.active.scale,
        this.active.offsetScale
      ) || this.active.scale;

    if (dir === 'height' || dir === 'width') {
      this.active.offset.x = 0;
      this.active.offset.y = 0;
    }

    this.active.scale = this.assetDisplayService.clipScale(this.active.scale, this.active.offsetScale);
    this.cachedScale = this.active.scale;
    this.calcAssetSizes();
  }

  calcAssetSizes(data: AssetPageData = this.active) {
    if (!data?.asset) return;
    this._containerOffset = this.containerOffset;
    const container = this.getContainerSize(data.asset);
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(data.asset.version_data, container);

    if (data.asset.type === 'video') {
      data.scale = this.getBaseZoom(data);
    }

    const { left, right, top, bottom } = this.assetDisplayService.getScaledBounds(
      data.asset.version_data,
      container,
      data.offset,
      data.scale
    );

    if (!this.dragging) {
      if (left < 0) data.offset.x += left * (1 / data.scale);
      else if (right < 0) data.offset.x -= right * (1 / data.scale);

      if (top < 0) data.offset.y += top * (1 / data.scale);
      else if (bottom < 0) data.offset.y -= bottom * (1 / data.scale);
    }

    const _data = data.asset.version_data.data?.width ? data.asset.version_data.data : this.getDataBackup();
    data.offsetScale = boundedContainer.width / (_data.width || 1);

    if (data === this.active) {
      const zoom = this.assetDisplayService.getBaseZoom(data.offsetScale, data.scale);
      this.fitButton = this.assetDisplayService.getButtonFit(data.asset.version_data, container, zoom, data.scale);
    }

    data._offsetY = data.offset.y;

    if (!this.closed) this.backgroundOpacity = 1;

    if (this.touchDragDirMode) {
      // clamping either x or y movement
      if (this.touchDragDirMode === 'x') {
        this.active._offsetY = 0;
      } else {
        this._containerOffset = 0;
        this.backgroundOpacity = 1 - (1 / window.innerHeight) * Math.abs(this.active._offsetY);
      }
    }

    if (this.active?.asset.id === data.asset.id) {
      const zoomed = data.scale > 1;
      if (zoomed) this.showWebGl = true;
      else this.showWebGl = false;
    }

    const s = data.offsetScale * data.scale;

    data.crop = new Rect(
      this.container.width / 2 - (_data.width * s) / 2 + data.offset.x * data.scale,
      this.container.height / 2 - (_data.height * s) / 2 + data.offset.y * data.scale,
      _data.width * s,
      _data.height * s
    );
    this.check.detectChanges();
  }

  close() {
    this.closed = true;
    if (!this.isDesktop) this.backgroundOpacity = 0;

    const path = getFullPath(this.route.snapshot);
    path.pop();
    if (this.state.pathHistory.includes('/' + path.join('/'))) {
      this.location.back();
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
    this.check.detectChanges();
  }

  private showControls(autoHide = true) {
    if (this.sideMenuOpen && !this.isDesktop) return;
    this.controlsVisible = true;
    this.autoHideControls = autoHide;
    this.lastShowControlsTime = Date.now();
    this.check.detectChanges();
    this.hideControls();
  }

  private touchToggleControls(event?: TouchEvent) {
    if (this.sideMenuOpen && !this.isDesktop && event) this.touchToggleControlsInstant(event);
    else this.touchToggleControlsDebounced(event);
  }

  private touchToggleControlsDebounced = debounce(
    (event?: TouchEvent) => this.touchToggleControlsInstant(event),
    doubleClickTime
  );

  private touchToggleControlsInstant(event: TouchEvent) {
    if (!event) return;
    const pos = Rect.fromTouch(event.changedTouches[0]);
    if (!this.touchStartPos) return;
    const diff = pos.subtract(this.touchStartPos);
    if (diff.magnitude() > 2) return;
    this.controlsVisible = !this.controlsVisible;
    this.check.detectChanges();

    if (this.sideMenuOpen && !this.isDesktop && event) {
      this.toggleSideMenu(this.sideMenuMode);
    }
  }

  private hideControls = debounce(() => {
    if (!this.isDesktop) return;
    if (this.sideMenuOpen) return;
    if (!this.autoHideControls) return;
    if (this.neverHideControls) return;
    if (this.colorLabelMenuOpen) return;
    if (this.dialogOpen) return;
    if (this.contextService.onContextOpen.value) return this.hideControls();
    this.controlsVisible = false;
    this.check.detectChanges();
  }, 2500);

  private handleEndCommentDragMode() {
    if (this.controlsVisible && this.isTouchDevice) {
      this.controlsVisible = false;
      this.check.detectChanges();
    }
    if (this.commentPlaceHoverRect) {
      const size = new Rect(this.commentPlaceHoverRect.width, this.commentPlaceHoverRect.height).magnitude();
      if (size < 10) {
        this.commentPlaceHoverRect.width = 0;
        this.commentPlaceHoverRect.height = 0;
        this.check.detectChanges();
      }
      this.analytics.track('comment-placed', false, {
        type: 'rect',
      });
      return this.startCommentAtPosition(this.commentPlaceHoverRect);
    }
    this.analytics.track('comment-placed', false, {
      type: 'point',
    });
    return this.startCommentAtPosition(this.commentPlaceStartPosition);
  }

  mouseDown(event: MouseEvent) {
    if (event.buttons !== 1 && event.buttons !== 4) return;
    if (event['sourceCapabilities']?.['firesTouchEvents']) return;

    if (this.commentPlacementMode && !this.spaceKeyDown && event.buttons !== 4) {
      this.commentPlacementMode = 'drag';
      this.commentPlaceStartPosition = Rect.fromMouseEvent(event);
      this.commentPlaceHoverRect = undefined;
      this.check.detectChanges();
      return;
    }

    const path = getEventPath(event);
    this.showControls(!path.find(el => el.classList?.contains('dont-hide-controls')));
    this.touchStartTime = Date.now();
    this.mouseIsDown = true;
    if (!elementIsInputLike(document.activeElement) && !elementIsInputLike(<Element>event.target)) {
      event.preventDefault(); // needed for firefox dragging to work
    }
    this.enableDragging();
    this.mouseHasMoved = false;
    this.check.detectChanges();
  }

  mouseUp(event: MouseEvent) {
    if (this.commentPlacementMode === 'drag') {
      return this.handleEndCommentDragMode();
    }

    if (!this.mouseIsDown) return;
    this.mouseIsDown = false;
    this.disableDragging();
    this.calcAssetSizes();
    const path = getEventPath(event);

    this.check.detectChanges();

    if (path.find(el => el instanceof HTMLButtonElement)) return;
    if (this.active.asset.type === 'image') {
      const touchTime = Date.now() - this.touchStartTime;
      if (!this.mouseHasMoved && touchTime < 200) {
        if (this.selectedThreadId) {
          this.comments?.selectThread(undefined);
          return;
        }

        const scalePercent = this.getScalePercent(this.active);
        if (scalePercent < 100 || scalePercent > 100) {
          if (this.active.scale <= 1) {
            const container = this.getContainerSize();

            const offsetX = (event.clientX - container.width / 2) / this.active.scale;
            const offsetY = (event.clientY - container.height / 2) / this.active.scale;

            this.active.offset.x += -offsetX;
            this.active.offset.y += -offsetY;
            this.active._offsetY += -offsetY;
          }

          this.onScale('full');
        } else this.onScale('fit');
      }
    }
  }

  mouseMove(event: MouseEvent) {
    if (!document.hasFocus()) return;
    if (event['sourceCapabilities']?.['firesTouchEvents']) return;
    if (this.isTouchDevice) return;
    if (!this.isDesktop && this.commentPlacementMode === 'place') return;
    if (this.commentPlacementMode === 'drag') {
      const pos = Rect.fromMouseEvent(event);
      const bounds = this.commentPlaceStartPosition.union(pos);
      if (new Rect(bounds.width, bounds.height).magnitude() < 10) return;
      this.commentPlaceHoverRect = bounds;
      this.check.detectChanges();
      return;
    }

    const path = getEventPath(event);
    this.showControls(!path.find(el => el.classList?.contains('dont-hide-controls')));
    if (!this.mouseIsDown) return;

    if (event.buttons === 4 && !this.dragging) this.enableDragging();

    if (event.buttons !== 1 && event.buttons !== 4) {
      if (this.dragging) this.mouseUp(event);
      return;
    }
    if (this.active.asset.type === 'video') return;
    this.active.offset.x += (event.movementX * 1) / this.active.scale; // / devicePixelRatio();
    this.active.offset.y += (event.movementY * 1) / this.active.scale; // / devicePixelRatio();
    this.mouseHasMoved = true;
    this.calcAssetSizes();
  }

  touchStart(event: TouchEvent) {
    if (this.commentPlacementMode) {
      this.commentPlacementMode = 'drag';
      this.commentPlaceStartPosition = Rect.fromTouchEvent(event)[0];
      this.commentPlaceHoverRect = undefined;
      this.check.detectChanges();
      return;
    }

    this.isTouchDevice = true;
    this.touchDragDirMode = undefined;
    this.currentNumberOfTouches = event.touches.length;
    this.previousTouchStartTime = this.touchStartTime;
    this.touchStartPos = Rect.fromTouchEvent(event)[0];
    this.previousTouches = Rect.fromTouchEvent(event);
    this.touchStartTime = Date.now();
    this.disableClickOutsideThreads = true;
    this.check.detectChanges();
  }

  touchMove(event: TouchEvent) {
    if (this.commentPlacementMode === 'drag') {
      const pos = Rect.fromTouchEvent(event)[0];
      const bounds = this.commentPlaceStartPosition.union(pos);
      if (new Rect(bounds.width, bounds.height).magnitude() < 10) return;
      this.commentPlaceHoverRect = bounds;
      this.check.detectChanges();
      return;
    }
    this.enableDragging();
    if (this.previousTouches?.length) {
      if (event.touches.length === 1) {
        if (this.previousTouches.length > 1) {
          // Without this the asset jumps around because previoustouches[0] might not be the same finger as event.touches[0]
          this.previousTouches = Rect.fromTouchEvent(event);
        }
        this.touchMoveSingle(event);
      } else this.touchMoveMultiple(event);
    }
    this.previousTouches = Rect.fromTouchEvent(event);
    this.check.detectChanges();
  }

  private touchMoveSingle(event: TouchEvent) {
    const touch = Rect.fromTouch(event.touches[0]);

    const { zoomedIn, offsetChange, diff } = this.assetDisplayService.touchMoveSingle(
      this.active.offsetScale,
      this.active.scale,
      touch,
      this.previousTouches[0]
    );

    if (zoomedIn) {
      this.active.offset.x += offsetChange.x;
    } else {
      if (this.mobileAnnotationViewingMode) return;
      this.noContainerAnimation = true;
      this.containerVelocity = diff[0];
      this.containerOffset += this.containerVelocity;

      if (!this.touchDragDirMode) {
        if (Math.abs(this.containerOffset) > 10) this.touchDragDirMode = 'x';
        if (Math.abs(this.active.offset.y) > 20) this.touchDragDirMode = 'y';
      }
    }

    this.active.offset.y += offsetChange.y;
    this.calcAssetSizes();
  }

  private touchMoveMultiple(event: TouchEvent) {
    if (!this.isDesktop && this.sideMenuOpen) return;
    const touches = Rect.fromTouchEvent(event);
    if (!touches.length || this.previousTouches.length < 2) return;

    const { offsetChange, scale } = this.assetDisplayService.touchMoveMultiple(
      event,
      this.previousTouches,
      this.active.scale,
      this.active.offsetScale
    );

    this.active.offset = this.active.offset.add(offsetChange);
    this.active.scale = scale;

    this.calcAssetSizes();
  }

  private isDoubleTap(event: TouchEvent) {
    if (this.active.asset.type !== 'image') return false;
    if (this.currentNumberOfTouches !== 0) return false;
    if (!this.previousTouchStartTime) return false;
    if (Date.now() - this.previousTouchStartTime > doubleClickTime) return false;
    const touchMoved = this.touchStartPos
      ? Rect.fromTouch(event.changedTouches[0]).subtract(this.touchStartPos)
      : new Rect();
    if (touchMoved.magnitude() > 20) return false;
    const touchTime = Date.now() - this.touchStartTime;
    if (touchTime > 200) return false;
    return true;
  }

  async touchEnd(event: TouchEvent) {
    if (this.commentPlacementMode === 'drag') {
      return this.handleEndCommentDragMode();
    }

    setTimeout(() => {
      this.disableClickOutsideThreads = false;
      this.check.detectChanges();
    }, 10);
    const action = this.getTouchEndAction(event);
    if (action === 'close') this.close();
    else if (action === 'switch-right') this.switchAsset(1);
    else if (action === 'switch-left') this.switchAsset(-1);
    else {
      this.containerOffset = 0;
      const touchedVideoWithControls =
        getEventPath(event).some(el => el.tagName === 'APP-VIDEO-VIEWER') && this.controlsVisible;
      if (!touchedVideoWithControls) {
        this.touchToggleControls(event);
      }
    }

    this.currentNumberOfTouches = event.touches.length;

    const disableZoomMobileSidemenu = !this.isDesktop && this.sideMenuOpen;

    if (this.isDoubleTap(event) && !disableZoomMobileSidemenu && this.active.asset.type === 'image') {
      this.touchToggleControls(undefined); // prevent double tap from showing controls
      const scalePercent = this.getScalePercent(this.active);
      if (scalePercent < 100 || scalePercent > 100) {
        if (this.active.scale <= 1) {
          const container = this.getContainerSize();
          const offsetX = this.touchStartPos.x - container.width / 2;
          const offsetY = this.touchStartPos.y - container.height / 2;
          this.active.offset.x = -offsetX;
          this.active.offset.y = -offsetY;
          this.active._offsetY = -offsetY;
        }

        this.onScale('full');
      } else this.onScale('fit');
    }

    if (this.currentNumberOfTouches === 0) {
      this.disableDragging(); // why is this down here?
      this.noContainerAnimation = false;
      this.previousTouches = [];
    }
    this.calcAssetSizes();
  }

  private getTouchEndAction(event: TouchEvent) {
    if (this.annotationMode) return;
    if (this.mobileAnnotationViewingMode) return;
    if (!this.touchStartPos) return;
    const touchMoved = Rect.fromTouch(event.changedTouches[0]).subtract(this.touchStartPos);

    if (this.active.scale <= 1) {
      const x = Math.abs(this._containerOffset);
      const y = Math.abs(this.active._offsetY);
      if (x > y) {
        if (Math.abs(this.containerVelocity) > 3 || x > window.innerWidth / 2) {
          return touchMoved.x > 0 ? 'switch-left' : 'switch-right';
        }
      } else {
        if (y > window.innerHeight / 4 || Math.abs(touchMoved.y) > 100) return 'close';
      }
    }
  }

  async toggleSideMenu(mode: 'info' | 'activity' | undefined) {
    if (mode === 'activity' && this.accessLevel === 'view_only') return;
    if (this.annotationBeingViewed) {
      this.viewAnnotation(undefined);
    }
    if (this.closed) return;
    if (this.sideMenuMode === mode) mode = undefined;
    if (mode) this.storage.set('asset-sidebar', mode);
    else this.storage.remove('asset-sidebar');
    this.sideMenuMode = mode;
    this.noContainerAnimation = true;
    this.sideMenuOpen = !!mode;
    if (this.commentPlacementMode !== 'place') {
      this.commentPlacementMode = undefined;
    }
    this.commentPlacePositionOverridden = false;
    this.selectedThreadId = undefined;
    this.hoverThreadId = undefined;
    this.calcCommentOverlayPosition();
    this.calcAssetSizes();
    this.container = this.getContainerSize();
    await this.handleContainerOffset();
    this.noContainerAnimation = false;
    if (this.sideMenuOpen) {
      this.router.navigate([], {
        fragment: mode === 'activity' ? 'comments' : 'info',
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      });
      if (!this.isDesktop) {
        this.controlsVisible = false;
        this.onScale('fit');
      }
    } else {
      this.router.navigate([], { replaceUrl: true, queryParamsHandling: 'preserve' });
      if (!this.isDesktop) {
        this.controlsVisible = true;
      }
    }
    this.check.detectChanges();
  }

  getBaseZoom(data: AssetPageData) {
    return this.assetDisplayService.getBaseZoom(data.offsetScale, getBaseZoom());
  }

  async onSwitchToAsset(asset: AssetDto) {
    this.noContainerAnimation = true;
    this.router.navigate(['../' + asset.id], {
      relativeTo: this.route,
      fragment: this.route.snapshot.fragment,
      replaceUrl: true,
      queryParamsHandling: 'preserve',
    });
    this.check.detectChanges();
    setTimeout(() => {
      this.noContainerAnimation = false;
      this.check.detectChanges();
    }, 100);
  }

  async switchAsset(change: number, clearAssets = false) {
    if (this.annotationMode) return;
    if (this.mobileAnnotationViewingMode) return;
    const index = this.assets.findIndex(img => img.id === this.active.asset.id);
    const newIndex = index + change;
    let asset = this.assets[newIndex];

    // Wrap from beginning to end
    if (newIndex === -1) asset = this.assets[this.assets.length - 1];
    // Wrap from end to beginning
    else if (newIndex === this.assets.length) {
      asset = this.assets[0];
    }
    return this.changeToAsset(asset, clearAssets);
  }

  async changeToAsset(asset: AssetDto, clearAssets: boolean) {
    if (!asset) return;
    if (clearAssets) this.clearAssets = true;

    this.active.scale = this.getBaseZoom(this.active);
    this.active.offset.x = 0;
    this.active.offset.y = 0;
    this.check.detectChanges();

    // Preemtively changing to avoid assets update from causing issues
    this.assetChange(asset, this.assets, this.sections);

    const queryParams = copy(this.route.snapshot.queryParams);
    delete queryParams.autoplay;
    await this.router.navigate(['../' + asset.id], {
      relativeTo: this.route,
      fragment: this.route.snapshot.fragment,
      replaceUrl: true,
      queryParams: queryParams,
    });
  }

  async updateAssetStatus(status: AssetStatus, isKeyboardShortcut = false) {
    if (this.gallery.enabled_asset_statuses && !this.gallery.enabled_asset_statuses.includes(status)) {
      if (status === 'rejected') status = 'no_status';
      else return;
    }
    if (isKeyboardShortcut) {
      this.analytics.reportKeyboardShortcut('asset-status', 'asset-page', { status });
    }
    return this.feedbackService.updateAssetStatus(this.active.asset, this.gallery, status, true);
  }

  async updateColorLabel(color_label: AssetColorLabel, isKeyboardShortcut = false) {
    if (isKeyboardShortcut) {
      const colorLabelName = this.gallery.color_labels?.find(l => l.color === color_label)?.name;
      this.analytics.reportKeyboardShortcut('color-label', 'asset-page', { color: color_label, colorLabelName });
    }
    await this.feedbackService.updateColorLabel(this.active.asset, this.gallery, color_label, true);
    this.currentLabel = this.colorLabels.find(l => l.color === this.active.asset.color_label);
    this.check.detectChanges();
  }

  async toggleLike() {
    await this.feedbackService.toggleLike(this.active.asset, this.gallery);
  }

  private getAfterDeleteChange() {
    if (this.assets.findIndex(i => i.id === this.active.asset.id) !== 0) return -1;
    else if (this.assets.length > 1) return 1;
  }

  afterDelete() {
    const dir = this.getAfterDeleteChange();
    if (dir) this.switchAsset(dir, true);
    else this.close();
  }

  onMovedAsset(asset: AssetDto) {
    const dir = this.getAfterDeleteChange();
    const index = this.assets.indexOf(asset);
    if (index < 0) return;
    this.assets.splice(index, 1);
    this.check.detectChanges();
    if (this.active.asset.id !== asset.id) return;
    if (dir) this.switchAsset(dir, true);
    else this.close();
  }

  getContainerSize(asset: AssetDto = this.active?.asset): Rect {
    const extraVerticalSpace = asset?.type === 'video' ? 120 : 0;
    const sideMenuOpen = this.sideMenuOpen || this.sideMenuWillBeOpen;

    if (!this.isDesktop) {
      return new Rect(
        0,
        0,
        window.innerWidth,
        sideMenuOpen ? window.innerHeight * 0.3 : window.innerHeight - extraVerticalSpace
      );
    }

    return new Rect(
      0,
      0,
      window.innerWidth - (sideMenuOpen ? assetSideMenuSize : 0),
      window.innerHeight - extraVerticalSpace
    );
  }

  getActiveIndex() {
    return this.assets.findIndex(img => img.id === this.active.asset.id);
  }

  toggleCommentPlacementMode() {
    if (this.commentPlacementMode) {
      this.commentPlacementMode = undefined;
      this.commentPlacePosition = undefined;
      this.commentPlacePositionOverridden = false;
      this.check.detectChanges();
    } else {
      this.switchPlaceComment(true);
    }
  }

  createCommentOverlayClose() {
    this.stopCommentAtPosition();
    if (!this.isDesktop && this.commentPlacementMode) {
      // on mobile we want to get out of comment placing mode when the overlay is closed
      this.toggleCommentPlacementMode();
    }
  }

  async startAnnotation() {
    if (!this.allowedAndEnabledFeedbackOptions.includes('annotation')) return;
    if (this.commentPlacementMode || this.commentPlacePosition) {
      this.switchPlaceComment(false);
      this.stopCommentAtPosition();
    }
    this.annotationMode = true;
    this.createAnnotationShapes = null;
    this.annotationTool = 'polygon';
    this.annotationColor = 'blue';
    this.showThumbnailBar = false;
    this.annotationBeingViewed = undefined;
    this.mobileAnnotationViewingMode = false;

    if (!this.isDesktop) {
      this.toggleSideMenu(undefined);
    }

    if (this.active.asset.type === 'video') {
      this.videoTimeService.setTime(this.videoTime.currentSecond);
    }
    this.check.detectChanges();
  }

  async stopAnnotation() {
    this.annotationMode = false;
    this.createAnnotationShapes = null;
    this.showCreateCommentOverlay = false;
    this.commentPlacePosition = undefined;
    this.commentPlacePositionOverridden = false;
    this.showControls();
    this.check.detectChanges();
  }

  toggleAnnotationMode() {
    if (this.annotationMode) this.stopAnnotation();
    else this.startAnnotation();
  }

  public annotationDone(send: boolean) {
    if (!send) return this.stopAnnotation();
    this.showCreateCommentOverlay = true;
    this.check.detectChanges();
  }

  annotationMouseDown() {
    this.showCreateCommentOverlay = false;
    this.check.detectChanges();
  }

  annotationShapesChanges(shapes: Shape[]) {
    this.createAnnotationShapes = shapes;
    if (shapes?.length) {
      if (this.isDesktop) {
        this.showCreateCommentOverlay = true;
        this.commentPlacePosition = getShapeCommentPosition(
          shapes,
          this.active,
          this.sideMenuOpen,
          true,
          this.getContainerSize().width,
          400
        );
        this.calcCommentOverlayPosition();
      } else {
        // on mobile we always put the avatar in the center of the annotation
        const bounds = getAnnotationBounds(shapes);
        bounds.x += bounds.width / 2;
        bounds.y += bounds.height / 2;
        this.commentPlacePosition = relativeToAbsolutePosition(bounds, this.active, this.sideMenuOpen, false);
      }
    } else {
      this.showCreateCommentOverlay = false;
    }
    this.check.detectChanges();
  }

  private startCommentAtPosition(position: Rect) {
    this.showCreateCommentOverlay = true;
    this.commentPlacementMode = 'place';
    this.commentPlacePosition = position;
    this.calcCommentOverlayPosition();
    this.check.detectChanges();
  }

  stopCommentAtPosition() {
    this.showCreateCommentOverlay = false;
    this.commentPlaceStartPosition = undefined;
    this.commentPlaceHoverRect = undefined;
    this.check.detectChanges();
  }

  private calcCommentOverlayPosition() {
    if (!this.showCreateCommentOverlay || !this.commentPlacePosition) return;
    const { pos, align } = getPointCommentPosition(this.commentPlacePosition, this.getContainerSize().width, 400);

    if (align === 'left') {
      this.createCommentOverlayPosition = pos.add(new Rect(-23, -19));
    } else if (align === 'right') {
      this.createCommentOverlayPosition = pos.add(new Rect(23, -19));
    } else {
      this.createCommentOverlayPosition = pos.add(new Rect(0, -19));
    }
    this.commentPlaceAlignment = align;
    this.check.detectChanges();
  }

  async onNewThread(thread: CommentThread) {
    if (!this.sideMenuOpen || this.sideMenuMode !== 'activity') {
      this.toggleSideMenu('activity');
      await sleep(0);
    }
    this.comments?.selectThread(thread);
    this.check.detectChanges();
  }

  async switchPlaceComment(enabled: boolean) {
    if (enabled) {
      this.activeVideoViewer?.pause();
    }
    if (enabled && this.annotationMode) {
      this.stopAnnotation();
    }
    if (enabled && !this.isDesktop) {
      await this.toggleSideMenu(undefined);
      this.controlsVisible = false;
    }

    this.commentPlacementMode = enabled ? 'place' : undefined;
    this.commentPlacePosition = undefined;
    this.commentPlacePositionOverridden = false;
    this.check.detectChanges();
  }

  async viewAnnotation(annotation: SimpleAnnotation) {
    if (annotation) {
      if (!this.videoTime && this.active.asset.type === 'video') {
        await this.activeVideoViewer?.initialPlayClick();
        await sleep(0);
      }
      this.stopAnnotation();
      this.analytics.track('view_annotation');
      this.zoomToAnnotation(annotation);
      if (annotation.frame && this.videoTime) {
        const time = this.videoTime.durationSeconds * annotation.frame;
        this.videoTimeService.setTime(time);
        this.threadBeingViewedTime = undefined;
        this.check.detectChanges();
        firstValueFrom(this.waitTillVideoTimeIsSetTo(time)).then(() => {
          this.threadBeingViewedTime = time;
          this.check.detectChanges();
        });
      }
    } else {
      this.restoreBackupZoomSettings();
      this.threadBeingViewedTime = undefined;
      this.analytics.track('hide_annotation');
      if (this.mobileAnnotationViewingMode) {
        this.toggleSideMenu('activity');
      }
    }
    this.annotationBeingViewed = annotation;
    this.check.detectChanges();
  }

  private restoreBackupZoomSettings() {
    if (this.backupZoomSettings) {
      this.active.scale = this.backupZoomSettings.scale;
      this.active.offset = this.backupZoomSettings.offset.copy();
      this.calcAssetSizes();

      this.backupZoomSettings = undefined;
      this.check.detectChanges();
    }
  }

  private zoomToAnnotation(annotation: SimpleAnnotation) {
    const timeAgo = new Date().getTime() - annotation.created_at * 1000;
    if (timeAgo < 1000) return;
    this.zoomToBounds(getAnnotationBounds(annotation.shapes));
  }

  public zoomToComment(thread: CommentThread) {
    if (!thread) {
      return this.restoreBackupZoomSettings();
    }
    const timeAgo = new Date().getTime() - thread.created_at * 1000;
    if (timeAgo < 1000) return;
    const x = Array.isArray(thread.position.x) ? thread.position.x : [thread.position.x];
    const y = Array.isArray(thread.position.y) ? thread.position.y : [thread.position.y];
    const rects = x.map((x, i) => new Rect(x, y[i]));
    this.zoomToBounds(getAnnotationBounds(rects));
  }

  private zoomToBounds(bounds: Rect) {
    if (!this.backupZoomSettings) {
      this.backupZoomSettings = {
        scale: this.active.scale,
        offset: this.active.offset.copy(),
      };
    }
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(
      this.active.asset.version_data,
      this.getContainerSize()
    );

    bounds.x *= boundedContainer.width;
    bounds.y *= boundedContainer.height;
    bounds.width *= boundedContainer.width;
    bounds.height *= boundedContainer.height;

    const scale = Math.min(boundedContainer.width / bounds.width, boundedContainer.height / bounds.height);

    const offsetX = bounds.x + bounds.width / 2 - boundedContainer.width / 2;
    const offsetY = bounds.y + bounds.height / 2 - boundedContainer.height / 2;

    this.active.scale = Math.min(scale * 0.9, 2);
    this.active.offset.x = -offsetX;
    this.active.offset.y = -offsetY;
    this.calcAssetSizes();
  }

  private waitTillVideoTimeIsSetTo(time: number) {
    return of(null).pipe(
      switchMap(() => {
        if (Math.abs(this.videoTime.currentSecond - time) > 0.001) return throwError(() => 'time not set');
        return of(true);
      }),
      retry({ count: 20, delay: 100 }),
      catchError(() => of(null))
    );
  }

  async videoTimeChangeWithoutAnnotation(fraction: number) {
    if (!this.videoTime && this.active.asset.type === 'video') {
      await this.activeVideoViewer?.initialPlayClick();
      await sleep(0);
    }
    const time = this.videoTime.durationSeconds * fraction;
    this.videoTimeService.setTime(time);
    this.threadBeingViewedTime = undefined;
    this.check.detectChanges();
    firstValueFrom(this.waitTillVideoTimeIsSetTo(time)).then(() => {
      this.threadBeingViewedTime = time;
      this.check.detectChanges();
    });
  }

  toggleThumbnailBar(open: boolean) {
    this.showThumbnailBar = open;
    this.check.detectChanges();
  }

  onPreviewMove(offset: Rect) {
    this.active.offset = offset;
    this.check.detectChanges();
  }

  async uploadVersion(_files: File[]) {
    if (this.accessLevel !== 'edit') return;
    const files = Array.from(_files);
    if (!files.length) return;
    if (files.length > 1) return this.toasts.add('Please upload only one asset', 'error');
    const file = files[0];
    const resp = this.uploadService.handleCanUpload([file]);
    if (!resp) return;

    const asset = this.active.asset;
    await this.uploadService.uploadNewVersion(asset, file);
    if (this.active.asset.id !== asset.id) return;
    await this.uploadService.waitForAssetProcessing(asset.id);
    this.afterUploadNewVersion();
  }

  async afterUploadNewVersion() {
    this.router.navigate(['./'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  private allowDownload(asset: AssetDto) {
    if (this.accessLevel === 'edit') return true;
    if (!this.gallery.download_single_asset) return false;
    if (this.gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  onRightClick(event: Event) {
    if (this.allowDownload(this.active.asset)) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onContextMenu(event: MouseEvent) {
    if (this.allowDownload(this.active.asset)) {
      // We want to allow right click if downloads are enabled and at least one resized option is enabled
      const { sizes } = getAllowedDownloadSizes(this.gallery.download_media_size, this.accessLevel, this.active.asset);
      if (sizes.length) return;
    }
    event.preventDefault();
    this.showCustomMenu = true;
    this.customMenuPos = new Rect(event.clientX, event.clientY);
    this.copyrightMessage = this.getCopyrightMessage();
    this.check.detectChanges();
  }

  getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  calcAssetDisplayWidth(data: AssetPageData) {
    const offsetScale = Math.min(1, data.offsetScale);
    if (data.asset.version_data.data?.width) {
      return data.asset.version_data.data.width * offsetScale;
    }
    return (this.getDataBackup().width / devicePixelRatio()) * offsetScale;
  }

  private getCopyrightMessage(): string {
    // const exif = this.exifService.getExif(this.active.asset);
    // if (exif?.copyright) return exif.copyright;
    // if (exif?.creator) return exif.creator;
    const tenant = this.gallery['tenant'] as TenantDto;
    if (tenant?.name) return tenant.name;
    return 'Copyright ' + new Date().getFullYear();
  }

  imageLoaded() {
    this.showLoadingIndicator = false;
    this.check.detectChanges();
  }

  doShowLoadingIndicator() {
    this.showLoadingIndicator = true;
    if (this.loadingIndicatorTimeout) clearTimeout(this.loadingIndicatorTimeout);
    this.loadingIndicatorTimeout = setTimeout(() => (this.showLoadingIndicator = false), 1000);
    this.check.detectChanges();
  }

  onTimeChange(time: VideoTimeData) {
    if (this.videoTime && objectsMatch(this.videoTime, time)) return;
    this.videoTime = time;

    if (this.threadBeingViewedTime) {
      const timeChanged = Math.abs(this.threadBeingViewedTime - time.currentSecond) > 0.001;
      if (timeChanged) {
        if (this.selectedThreadId) {
          this.comments?.selectThread(undefined);
          this.annotationBeingViewed = undefined;
          this.mobileAnnotationViewingMode = false;
        }
      }
    }
    this.check.detectChanges();
  }

  async selectThreadFromFace(thread: CommentThread) {
    if (!this.sideMenuOpen || this.sideMenuMode !== 'activity') {
      this.toggleSideMenu('activity');
    }
    await sleep(0);
    this.comments?.toggleSelectedThread(thread);
    this.check.detectChanges();
  }

  public get showAnnotationControls() {
    if (!this.annotationMode) return false;
    if (this.isDesktop) return true;
    return !this.showCreateCommentOverlay;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
